import React, { useMemo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import Text from '@/components-latest/primitives/Text'
import Spinner from '@/components-latest/primitives/Spinner'
import AlertText from '@/components-latest/primitives/AlertText'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Store from '@/components-latest/primitives/Icons/Store'

import Styles from './PreparationTime.module.scss'

type Props = {
  preparationTime?: number
  isLoading?: boolean
  className?: string
  title?: string
}

const PreparationTime: React.FC<Props> = React.memo(
  ({ preparationTime = 0, isLoading = false, className, title }) => {
    const { t } = useTranslation()

    const totalSeconds = Math.floor(preparationTime / 1000)
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
    const seconds = String(totalSeconds % 60).padStart(2, '0')

    const timeRanges = useMemo(
      () => [
        {
          id: 'fast',
          min: 0,
          max: 600000,
          className: Styles.green,
          label: t('performance.prep-time.fast'),
          alert: undefined
        },
        {
          id: 'medium',
          min: 600001,
          max: 900000,
          className: Styles.yellow,
          label: t('performance.prep-time.medium'),
          alert: t('performance.prep-time.medium-alert')
        },
        {
          id: 'slow',
          min: 900001,
          max: Number.MAX_VALUE,
          className: Styles.red,
          label: t('performance.prep-time.slow'),
          alert: t('performance.prep-time.slow-alert')
        }
      ],
      [t]
    )

    const activeRange = useMemo(
      () => timeRanges.find(({ min, max }) => preparationTime >= min && preparationTime <= max),
      [preparationTime, timeRanges]
    )

    if (preparationTime === null || preparationTime === undefined) return null

    return (
      <ContentBlock className={clsx(Styles.card, className)}>
        <div className={Styles.row}>
          {/* Title / Branch */}
          <div className={clsx(Styles.titleCell, { [Styles.noTitle]: !title })}>
            {title && <Store />}
            <Text variant="body" asChild>
              <span>{title}</span>
            </Text>
          </div>

          {/* Separator */}
          <div className={Styles.separator} />

          {/* Metrics */}
          <div className={Styles.timeCell}>
            {isLoading ? (
              <div className={Styles.loader}>
                <Spinner />
              </div>
            ) : (
              <>
                <Text
                  className={Styles.time}
                  variant="nextTitle-1"
                  weight="extraBold"
                  asChild>
                  <p>{t('performance.prep-time.time', { minutes, seconds })}</p>
                </Text>

                <div className={Styles.ranges}>
                  {timeRanges.map(({ id, className, label }) => {
                    const isActive = id === activeRange?.id
                    return (
                      <div
                        key={id}
                        className={clsx(Styles.range, { [Styles.active]: isActive })}
                        data-active={isActive}>
                        <div className={clsx(Styles.line, { [className]: isActive })} />
                        <Text variant="nextSubtitle" weight="regular" asChild>
                          <p>{label}</p>
                        </Text>
                      </div>
                    )
                  })}
                </div>

                {activeRange?.alert && (
                  <AlertText
                    className={Styles.alert}
                    text={activeRange.alert}
                    variant={activeRange.id === 'slow' ? 'error' : 'warning'}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </ContentBlock>
    )
  }
)

PreparationTime.displayName = 'PreparationTime'
export default PreparationTime
